/**
 * Centralized error reporting — thin wrapper around Sentry.captureException.
 *
 * All business-logic catch blocks should call reportError() rather than
 * importing Sentry directly. This keeps the Sentry dependency behind a single
 * file so swapping providers later is a one-file change.
 *
 * Rules for what to report:
 *  ✅ Unexpected runtime failures (API 5xx, JS errors, promise rejections)
 *  ✅ Security-relevant failures (403 from adventure/world-builder = suspected IDOR)
 *  ❌ User-driven non-errors (404 "not found", 401 unauthenticated, AbortError from
 *     client-side cancel, network offline — these are expected and high-volume)
 *
 * Usage:
 *   import { reportError } from "@/lib/error-reporter";
 *   reportError(err, "adventure:sendTurn", { adventureId, worldId });
 */

import * as Sentry from "@sentry/react";

/** Error classes that are expected and should not be sent to Sentry. */
const SUPPRESSED_NAMES = new Set(["AbortError", "NetworkError"]);
const SUPPRESSED_MESSAGES = [
  "Failed to fetch",
  "Load failed",
  "NetworkError",
  "The operation was aborted",
];

function isSuppressed(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (SUPPRESSED_NAMES.has(err.name)) return true;
  return SUPPRESSED_MESSAGES.some((m) => err.message?.includes(m));
}

/**
 * Report an error to Sentry (when DSN is configured) with optional context.
 *
 * @param err     - The caught error (any type).
 * @param context - Short slug identifying where the error occurred, e.g. "adventure:sendTurn".
 * @param extra   - Optional extra context (IDs, counts, etc.).
 */
export function reportError(
  err: unknown,
  context: string,
  extra?: Record<string, unknown>,
): void {
  if (isSuppressed(err)) return;

  const tags: Record<string, string> = { context };
  const enrichedExtra = { context, ...extra };

  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      scope.setTags(tags);
      scope.setExtras(enrichedExtra);
      Sentry.captureException(err);
    });
  } else {
    // Local dev: log to console so developers see what would be reported.
    console.error(`[reportError:${context}]`, err, enrichedExtra);
  }
}
